import AppTableControl from '@/components/AppTableControl';
import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getPrizeStats, getPrizeStatsTracking } from '@/services/revenue.service';
import { formatDate, formatPeriod, columnSorter } from '@/utils/formatters';
import { Space } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import usePrizeOptionsQuery from '@/hooks/usePrizeOptionsQuery';
import DetailModal from '@/pages/PrizeStats/DetailModal';
import PrizeTable from '@/pages/PrizeStats/PrizeTable';
import PieceTrackingTable from '@/pages/PrizeStats/PieceTrackingTable';
import { getActiveBadge } from '@/utils/getActiveBadge';
import _, { isArray } from 'lodash';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

const START_DATE = dayjs().startOf('month').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');
const prizeWinnerCols = [
  {
    title: 'No.',
    dataIndex: 'id',
    key: 'id',
    sorter: columnSorter('id'),
    align: 'center',
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Prize',
    dataIndex: 'name',
    key: 'name',
    width: 230,
    sorter: columnSorter('name'),
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Period',
    key: 'period',
    render: (record) => (
      <span>{formatPeriod(record.period_start_date, record.period_end_date)}</span>
    ),
  },
  {
    title: 'Winner Phone Number',
    dataIndex: 'isdn',
    key: 'isdn',
    sorter: columnSorter('isdn'),
    align: 'center',
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Redemption Time',
    dataIndex: 'created_at',
    key: 'created_at',
    sorter: columnSorter('created_at', 'date'),
    render: (date) => <span>{formatDate(date)}</span>,
  },
  {
    title: 'Status',
    // dataIndex: 'status',
    key: 'status',
    render: (record) =>
      getActiveBadge(true, {
        activeText: 'Claimed',
      }),
    sorter: columnSorter('status'),
  },
];

function PrizeStats() {
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);
  const [openTracking, setOpenTracking] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [date, setDate] = useState([START_DATE, END_DATE]);
  const [selectedPrizeOption, setSelectedPrizeOption] = useState(null);
  const [selectedPrizeRecord, setSelectedPrizeRecord] = useState(null);
  const [recordDetail, setRecordDetail] = useState({});
  const [highlight, setHighlight] = useState(false);
  const [searchPhone, setSearchPhone] = useState(null);
  const trackingCardRef = useRef();

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getPrizeStats({
        startDate: date?.[0],
        endDate: date?.[1],
        // prizeId: selectedPrizeOption,
        // isdn: searchPhone,
      }),
    [
      'prizeStats',
      date,
      // selectedPrizeOption,
      // searchPhone
    ]
  );

  const {
    data: trackingData,
    isLoading: isTrackingLoading,
    refetch: refetchTracking,
    isRefetching: isTrackingRefetching,
  } = usePaginatedQuery(
    () => getPrizeStatsTracking({ id: selectedPrizeRecord?.id }),
    ['prizeStatsTracking', selectedPrizeRecord?.id],
    {
      enabled: !!selectedPrizeRecord?.id,
    }
  );

  const {
    data: prizeData,
    isLoading: isLoadingPrizes,
    // refetch: refetchPrizes,
    isRefetching: isRefetchPrizes,
  } = usePrizeOptionsQuery(date?.[0], date?.[1]);

  // const {
  //   data: trackingDetails,
  //   isLoading: isLoadingTrackingDetails,
  //   refetch: refetchTrackingDetails,
  //   isRefetching: isRefetchingTrackingDetails,
  // } = usePaginatedQuery(() => )

  const prizeStatsData = useMemo(() => {
    if (_.isEmpty(data) || !isArray(data) || !data) {
      return [];
    }

    return data
      .filter((item) => {
        if (searchPhone) {
          return item.isdn.includes(searchPhone);
        }

        return item;
      })
      .filter((item) => {
        if (selectedPrizeOption) {
          return item.prize_id === selectedPrizeOption;
        }

        return item;
      });
  }, [data, searchPhone, selectedPrizeOption]);

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // const offset = (pageIndex - 1) * pageSize + 1;

  const handleOnRefresh = () => {
    refetch();
  };

  useEffect(() => {
    if (trackingCardRef.current && openTracking) {
      trackingCardRef.current.scrollIntoView({ behavior: 'smooth' });
      setHighlight(true);
      // Remove highlight after 1s
      const timer = setTimeout(() => setHighlight(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [openTracking]);

  const handleOnPrizeNameClick = (record) => {
    setOpenTracking(true);
    setSelectedPrizeRecord(record);
  };

  const filterOptions = useMemo(() => {
    return [
      {
        value: null,
        label: 'Select',
      },
      ...(Array.isArray(prizeData) ? prizeData : []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    ];
  }, [prizeData]);

  return (
    <Space className="w-full" direction="vertical">
      <AppPageTitle title="Prize Statistics" />
      <AppTableControl
        selectOptions={filterOptions}
        selectLabel="Prize"
        optionsLabel="Prize"
        selectValue={selectedPrizeOption}
        setSelectValue={setSelectedPrizeOption}
        date={date}
        setDate={(value) => {
          if (openTracking) {
            setOpenTracking(false);
          }
          if (openDetail) {
            setOpenDetail(false);
          }
          setDate(value);
        }}
        dateLimit={{
          value: 30,
          unit: 'day',
        }}
        setPageIndex={setPageIndex}
        noStyle
        hasPhone
        setSearchPhone={setSearchPhone}
        searchPhone={searchPhone}
      />
      <AppTable
        tableTitle={<h3 className="font-medium">Prize Winner</h3>}
        columns={prizeWinnerCols}
        dataSource={
          // data?.data?.sort(
          //   (a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()
          // ) || []
          prizeStatsData
        }
        // pageIndex={pageIndex}
        // pageSize={pageSize}
        // onPageChange={(page) => setPageIndex(page)}
        // onPageSizeChange={(size) => setPageSize(size)}
        pagination={{
          current: pageIndex,
          pageSize: pageSize,
          total: data?.length || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          onChange: (page, size) => {
            setPageIndex(page);
            setPageSize(size);
          },
          size: isMobile ? 'small' : 'default',
          showTotal: (total, range) => {
            return `${range[0]}-${range[1]} of ${total} items`;
          },
          style: {
            gap: '4px',
          },
        }}
        loading={isLoading || isRefetching}
        totalItems={data?.length || 0}
        onRefresh={handleOnRefresh}
        hasPagination={false}
      />

      <PrizeTable
        dataSource={
          prizeData?.filter((item) => {
            if (selectedPrizeOption === null) {
              return item;
            } else {
              return item.id === selectedPrizeOption;
            }
          }) || []
        }
        loading={isLoadingPrizes || isRefetchPrizes}
        onClick={handleOnPrizeNameClick}
      />

      {openTracking && (
        <PieceTrackingTable
          prizeData={prizeData}
          selectedPrizeOption={selectedPrizeOption}
          selectedPrizeRecord={selectedPrizeRecord}
          setOpenDetail={setOpenDetail}
          setRecordDetail={setRecordDetail}
          setOpenTracking={setOpenTracking}
          highlight={highlight}
          dataSource={trackingData?.dataTracking}
          loading={isTrackingLoading || isTrackingRefetching}
          refetch={refetchTracking}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          ref={trackingCardRef}
        />
      )}

      <DetailModal
        open={openDetail}
        setOpen={setOpenDetail}
        trackingData={trackingData}
        recordDetail={recordDetail}
        selectedPrizeRecord={selectedPrizeRecord}
        setRecordDetail={setRecordDetail}
      />
    </Space>
  );
}

export default PrizeStats;
